import { createFeature, createReducer, on } from '@ngrx/store';

import { Opportunity, OppStage } from '@core/models';
import { OpportunitiesApiActions, OpportunitiesPageActions } from './opportunities.actions';

/**
 * `opportunities` feature state — the recruiting/sales funnel. `opportunities`
 * are the cards, `stages` the ordered pipeline columns they sit in. All edits
 * are session-only (no write-back to the data layer).
 */
export interface OpportunitiesState {
  readonly opportunities: Opportunity[];
  readonly stages: OppStage[];
  readonly loading: boolean;
  readonly error: string | null;
}

const initialState: OpportunitiesState = {
  opportunities: [],
  stages: [],
  loading: false,
  error: null,
};

export const opportunitiesFeature = createFeature({
  name: 'opportunities',
  reducer: createReducer(
    initialState,
    on(OpportunitiesPageActions.entered, (state) => ({ ...state, loading: true, error: null })),
    on(OpportunitiesApiActions.loadSuccess, (state, { opportunities, stages }) => ({
      ...state,
      opportunities,
      stages,
      loading: false,
    })),
    on(OpportunitiesApiActions.loadFailure, (state, { error }) => ({ ...state, loading: false, error })),
    // Upsert: replace the matching id in place, or append when it's new.
    on(OpportunitiesPageActions.saved, (state, { opportunity }) => ({
      ...state,
      opportunities: state.opportunities.some((o) => o.id === opportunity.id)
        ? state.opportunities.map((o) => (o.id === opportunity.id ? opportunity : o))
        : [...state.opportunities, opportunity],
    })),
    on(OpportunitiesPageActions.deleted, (state, { id }) => ({
      ...state,
      opportunities: state.opportunities.filter((o) => o.id !== id),
    })),
    on(OpportunitiesPageActions.stageChanged, (state, { id, stage }) => ({
      ...state,
      opportunities: state.opportunities.map((o) => (o.id === id ? { ...o, stage } : o)),
    })),
    // Flip the done flag on one next-step within one opportunity's checklist.
    on(OpportunitiesPageActions.stepToggled, (state, { id, stepId }) => ({
      ...state,
      opportunities: state.opportunities.map((o) =>
        o.id === id
          ? { ...o, nextSteps: o.nextSteps.map((s) => (s.id === stepId ? { ...s, done: !s.done } : s)) }
          : o,
      ),
    })),
    on(OpportunitiesPageActions.stagesChanged, (state, { stages }) => {
      const ids = new Set(stages.map((s) => s.id));
      const first = stages[0]?.id ?? 'lead';
      return {
        ...state,
        stages,
        // Opportunities on a removed stage fall back to the first stage.
        opportunities: state.opportunities.map((o) => (ids.has(o.stage) ? o : { ...o, stage: first })),
      };
    }),
  ),
});

export const { selectOpportunities, selectStages, selectLoading } = opportunitiesFeature;
